import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Transaction, Account } from '../services/plaidService';
import { cachedTransactions, cachedAccounts } from '../services/prefetch';

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Memoized account lookup maps for better performance
  const accountMap = useMemo(() => {
    const map = new Map<string, Account>();
    accounts.forEach(acc => map.set(acc.account_id, acc));
    return map;
  }, [accounts]);

  const getAccountType = useCallback((accountId: string) => {
    return accountMap.get(accountId)?.type || 'unknown';
  }, [accountMap]);

  const getAccountName = useCallback((accountId: string) => {
    return accountMap.get(accountId)?.name || 'Unknown Account';
  }, [accountMap]);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startDateStr = startDate ? startDate.toISOString().split('T')[0] : undefined;
      const endDateStr = endDate ? endDate.toISOString().split('T')[0] : undefined;

      // Fetch both transactions and accounts
      const [transactionsResponse, accountsResponse] = await Promise.all([
        cachedTransactions(startDateStr, endDateStr),
        cachedAccounts()
      ]);

      const allTransactions = transactionsResponse.transactions || [];
      setAllTransactions(allTransactions);
      setAccounts(accountsResponse.accounts || []);
      
      // Apply account filter if selected
      const filteredTransactions = selectedAccountId 
        ? allTransactions.filter(t => t.account_id === selectedAccountId)
        : allTransactions;
      
      setTransactions(filteredTransactions);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch transactions');
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getAmountColor = (amount: number) => {
    return amount > 0 ? 'success.main' : 'error.main';
  };

  const getDisplayAmount = (transaction: Transaction) => {
    const accountType = getAccountType(transaction.account_id);
    const originalAmount = transaction.amount;
    
    // According to Plaid docs:
    // - Positive values = money moves OUT of account (spending)
    // - Negative values = money moves IN to account (income/payments)
    
    if (accountType === 'credit') {
      // For credit cards: invert the amount to show user-friendly view
      // Plaid positive = purchase (spending) = show as negative
      // Plaid negative = payment (debt reduction) = show as positive
      return -originalAmount;
    }
    
    // For depository accounts (checking, savings):
    // Plaid positive = money out (spending) = show as negative
    // Plaid negative = money in (income) = show as positive
    return -originalAmount;
  };

  const getAmountColorForAccount = (transaction: Transaction) => {
    const accountType = getAccountType(transaction.account_id);
    const amount = getDisplayAmount(transaction);
    
    if (accountType === 'credit') {
      // For credit cards: positive = payment (good), negative = purchase (spending)
      return amount > 0 ? 'success.main' : 'error.main';
    } else {
      // For depository accounts: positive = income, negative = spending
      return amount > 0 ? 'success.main' : 'error.main';
    }
  };

  const handleAccountFilter = useCallback((accountId: string | null) => {
    setSelectedAccountId(accountId);
    setPage(0); // Reset to first page when filtering
    const filteredTransactions = accountId 
      ? allTransactions.filter(t => t.account_id === accountId)
      : allTransactions;
    setTransactions(filteredTransactions);
  }, [allTransactions]);

  const getAccountIcon = (accountType: string) => {
    switch (accountType) {
      case 'credit':
        return '💳';
      case 'depository':
        return '🏦';
      case 'loan':
        return '🏠';
      default:
        return '💰';
    }
  };

  // Memoized calculations for totals - only recalculate when transactions change
  const { totalSpent, totalIncome } = useMemo(() => {
    let spent = 0;
    let income = 0;
    
    transactions.forEach(t => {
      const accountType = getAccountType(t.account_id);
      const originalAmount = t.amount;
      const displayAmount = accountType === 'credit' ? -originalAmount : -originalAmount;
      
      if (displayAmount < 0) {
        spent += Math.abs(displayAmount);
      } else {
        income += displayAmount;
      }
    });
    
    return { totalSpent: spent, totalIncome: income };
  }, [transactions, getAccountType]);

  // Paginated transactions - only show current page
  const paginatedTransactions = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return transactions.slice(startIndex, startIndex + rowsPerPage);
  }, [transactions, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCategoryChips = (transaction: Transaction) => {
    const chips = [];
    
    // Handle new Plaid personal_finance_category format
    if (transaction.personal_finance_category) {
      const { primary, detailed } = transaction.personal_finance_category;
      
      // Primary category chip
      chips.push(
        <Chip
          key="primary"
          label={primary}
          size="small"
          variant="filled"
          color="primary"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      );
      
    }
    // Fallback to legacy category format
    else if (transaction.category && transaction.category.length > 0) {
      return transaction.category.slice(0, 2).map((category, index) => (
        <Chip
          key={index}
          label={category}
          size="small"
          variant="outlined"
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      ));
    }
    
    return chips.length > 0 ? chips : null;
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchTransactions}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" className="animate-fade-in">
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            gutterBottom
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1
            }}
          >
            Transactions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your income and expenses
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Card 
            sx={{ 
              flex: '1 1 300px', 
              minWidth: '300px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(239, 68, 68, 0.3)',
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Total Spent
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {formatAmount(totalSpent)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Outgoing transactions
              </Typography>
            </CardContent>
          </Card>
          <Card 
            sx={{ 
              flex: '1 1 300px', 
              minWidth: '300px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(16, 185, 129, 0.3)',
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Total Income
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {formatAmount(totalIncome)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Incoming transactions
              </Typography>
            </CardContent>
          </Card>
          <Card 
            sx={{ 
              flex: '1 1 300px', 
              minWidth: '300px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)',
              }
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Net Amount
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {formatAmount(totalIncome - totalSpent)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                Total balance change
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Date Filters */}
        <Card 
          sx={{ 
            mb: 4,
            background: 'white',
            border: '2px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Filter Transactions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Account</InputLabel>
                  <Select
                    value={selectedAccountId || ''}
                    onChange={(e) => handleAccountFilter(e.target.value || null)}
                    label="Filter by Account"
                  >
                    <MenuItem value="">
                      <em>All Accounts</em>
                    </MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.account_id} value={account.account_id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>{getAccountIcon(account.type)}</span>
                          <span>{account.name}</span>
                          <Chip 
                            label={account.type} 
                            size="small" 
                            color={account.type === 'credit' ? 'error' : account.type === 'depository' ? 'success' : 'default'}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                <Button
                  variant="contained"
                  onClick={fetchTransactions}
                  fullWidth
                >
                  Apply Filters
                </Button>
              </Box>
              {selectedAccountId && (
                <Box sx={{ flex: '1 1 150px', minWidth: '150px' }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleAccountFilter(null)}
                    fullWidth
                  >
                    Clear Account Filter
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card
          sx={{
            background: 'white',
            border: '2px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Transaction History
              </Typography>
              <Chip 
                label={`${transactions.length} of ${allTransactions.length} transactions`}
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              {selectedAccountId && (
                <Chip 
                  label={`Filtered by ${getAccountName(selectedAccountId)}`} 
                  size="small" 
                  color="secondary" 
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            
            {transactions.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 90 }}>Date</TableCell>
                      <TableCell align="right" sx={{ minWidth: 100 }}>Amount</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Account</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>Merchant</TableCell>
                      <TableCell sx={{ minWidth: 150 }}>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTransactions.map((transaction) => (
                      <TableRow
                        key={transaction.transaction_id}
                        onClick={() => setSelectedTransaction(transaction)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                      >
                        <TableCell>
                          {formatDate(transaction.date)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={getAmountColorForAccount(transaction)}
                          >
                            {formatAmount(getDisplayAmount(transaction))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{getAccountIcon(getAccountType(transaction.account_id))}</span>
                            <Box>
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={(e) => { e.stopPropagation(); handleAccountFilter(transaction.account_id); }}
                              >
                                {getAccountName(transaction.account_id)}
                              </Typography>
                              <Chip
                                label={getAccountType(transaction.account_id)}
                                size="small"
                                color={getAccountType(transaction.account_id) === 'credit' ? 'error' : getAccountType(transaction.account_id) === 'depository' ? 'success' : 'default'}
                                sx={{ fontSize: '0.7rem', height: '20px' }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {transaction.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {transaction.logo_url ? (
                              <Avatar
                                src={transaction.logo_url}
                                alt={transaction.merchant_name || ''}
                                sx={{ width: 24, height: 24 }}
                                variant="rounded"
                              />
                            ) : transaction.merchant_name ? (
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: 'primary.light'
                                }}
                                variant="rounded"
                              >
                                {transaction.merchant_name.charAt(0).toUpperCase()}
                              </Avatar>
                            ) : null}
                            <Typography variant="body2" color="textSecondary">
                              {transaction.merchant_name || '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {getCategoryChips(transaction)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={transactions.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            ) : (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" gutterBottom>
                  No transactions found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your date filters or check if your account is properly linked.
                </Typography>
                <Button variant="contained" onClick={fetchTransactions} sx={{ mt: 2 }}>
                  Refresh
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
        {/* Transaction Detail Dialog */}
        <Dialog
          open={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {selectedTransaction && (() => {
            const t = selectedTransaction;
            const displayAmount = getDisplayAmount(t);
            const isSpending = displayAmount < 0;
            const accentColor = isSpending ? '#ef4444' : '#10b981';
            const matchedAccount = accountMap.get(t.account_id);
            return (
              <>
                <DialogTitle
                  sx={{
                    background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}08 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                      {t.logo_url ? (
                        <Avatar src={t.logo_url} alt={t.merchant_name || ''} sx={{ width: 48, height: 48 }} variant="rounded" />
                      ) : (
                        <Avatar sx={{ width: 48, height: 48, bgcolor: accentColor, fontSize: '1.1rem', fontWeight: 700 }} variant="rounded">
                          {(t.merchant_name || t.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700} noWrap>
                          {t.merchant_name || t.name}
                        </Typography>
                        {t.merchant_name && t.merchant_name !== t.name && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {t.name}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton onClick={() => setSelectedTransaction(null)} size="small">
                      <Close />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                  <Box sx={{ textAlign: 'center', py: 2, mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      {isSpending ? 'Amount Spent' : 'Amount Received'}
                    </Typography>
                    <Typography variant="h3" fontWeight={800} sx={{ color: accentColor, fontVariantNumeric: 'tabular-nums' }}>
                      {isSpending ? '-' : '+'}{formatAmount(Math.abs(displayAmount))}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t.name}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        Date
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {formatDate(t.date)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                        Type
                      </Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                        {isSpending ? 'Expense' : 'Income'}
                      </Typography>
                    </Box>
                    {matchedAccount && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                          Account
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {matchedAccount.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {matchedAccount.subtype}
                        </Typography>
                      </Box>
                    )}
                    {t.merchant_name && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                          Merchant
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                          {t.merchant_name}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {(t.personal_finance_category || (t.category && t.category.length > 0)) && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1 }}>
                        Category
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {t.personal_finance_category ? (
                          <>
                            <Chip label={t.personal_finance_category.primary} size="small" color="primary" />
                            {t.personal_finance_category.detailed && t.personal_finance_category.detailed !== t.personal_finance_category.primary && (
                              <Chip label={t.personal_finance_category.detailed} size="small" variant="outlined" />
                            )}
                          </>
                        ) : (
                          t.category?.slice(0, 3).map((cat, i) => (
                            <Chip key={i} label={cat} size="small" variant="outlined" />
                          ))
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                      Transaction ID: {t.transaction_id}
                    </Typography>
                  </Box>
                </DialogContent>
              </>
            );
          })()}
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default Transactions;
